import { enableProdMode, LOCALE_ID } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import * as Sentry from '@sentry/browser';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { APP_BASE_HREF } from '@angular/common';
import { getLCP, getFID, getCLS } from 'web-vitals';
import { GtmSalesLineEnum, GtmSolutionEnum, GtmTemplateTypeEnum } from '~shared/modules/ga-tracking/constants';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';

const CLS_SCALE = 1000;

const sendToGTM = ({ name, value }) => {
  const dataLayer = ((window as any).dataLayer = (window as any).dataLayer || []);
  const country = (environment.countryDomainCode() as string).toUpperCase();
  dataLayer.push({
    event: 'coreWebVitals',
    category: 'coreWebVitals',
    action: name,
    // Google Analytics metrics must be integers, so the value is rounded.
    // For CLS the value is first multiplied by 1000 for greater precision
    // (note: increase the multiplier for greater precision if needed).
    value: Math.round(name === 'CLS' ? value * CLS_SCALE : value),
    // The 'id' value will be unique to the current page load. When sending
    // multiple values from the same page (e.g. for CLS), Google Analytics can
    // compute a total by grouping on this ID (note: requires `eventLabel` to
    // be a dimension in your report).
    label: 'Angular',
    GA360_solution: GtmSolutionEnum.MM_marketplace,
    GA360_country: country,
    GA360_salesLine: GtmSalesLineEnum[country],
    GA360_templateType: GtmTemplateTypeEnum.Other,
  });
};

getCLS(sendToGTM);
getFID(sendToGTM);
getLCP(sendToGTM);

if (environment.production) {
  enableProdMode();
}

declare let APP_ENV_VARIABLES;
(window as any).ssr = false;

const sentryDsn = APP_ENV_VARIABLES?.sentryDsn;

if (sentryDsn) {
  Sentry.init({
    dsn: APP_ENV_VARIABLES?.sentryDsn,
    environment: APP_ENV_VARIABLES?.appEnv || 'development',
    release: APP_ENV_VARIABLES?.sentryRelease || 'web-app-buyer@r000000',
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration && Sentry.browserTracingIntegration(),
      Sentry.replayIntegration && Sentry.replayIntegration(),
    ].filter(Boolean),
  });

  Sentry.setTag('session_id', CheckoutCommonUtil.getSessionIdFromSessionStorage())

  Sentry.captureEvent({
    message: `Initial page url ${window.location.href}`
  });
}

// Hook into History API
const origPushState = history.pushState;
history.pushState = function(state, title, url) {
  if (sentryDsn && Sentry.isInitialized()) {
    Sentry.captureException(new Error(`pushState navigation occurred. From ${window.location.href} to ${url}`));
  }

  return origPushState.apply(this, arguments);
};

const origReplaceState = history.replaceState;
history.replaceState = function(state, title, url) {
  if (sentryDsn && Sentry.isInitialized()) {
    Sentry.captureException(new Error(`replaceState navigation occurred. From ${window.location.href} to ${url}`));
  }

  return origReplaceState.apply(this, arguments);
};

// Listen to back/forward
window.addEventListener('popstate', function(e) {
  if (sentryDsn && Sentry.isInitialized()) {
    Sentry.captureMessage(`Back/forward navigation (popstate). Url: ${e.state}`);
  }
});

// @ts-ignore
__webpack_public_path__ = APP_ENV_VARIABLES ? `${APP_ENV_VARIABLES.appStaticUrl}/static/` : '/static/';

document.addEventListener('DOMContentLoaded', () => {
  const start = () => {
    (window as any).config = APP_ENV_VARIABLES;
    platformBrowserDynamic([
      {
        provide: LOCALE_ID,
        useValue: APP_ENV_VARIABLES.locale,
      },
      {
        provide: APP_BASE_HREF,
        useValue: APP_ENV_VARIABLES.baseHref,
      },
    ])
      .bootstrapModule(AppModule)
      .catch((err) => {
        console.error('Bootstrap error:', err);
        Sentry.captureException(err);
      });
  };
  start();
});
